import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Table, Form, Button, Pagination, Alert, Badge, InputGroup } from 'react-bootstrap';
import { Search, Eye, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { applicationService } from '../services/applicationService';
import './Email.css';

const ApplicationsList = forwardRef(({ onViewApplication, onStatusChange }, ref) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });

  // Load applications
  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await applicationService.getAllApplications({
        ...filters,
        status: filters.status === 'all' ? undefined : filters.status,
        page: currentPage,
        limit
      });

      if (response.success && response.data) {
        setApplications(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotal(response.pagination?.total || 0);
      }
    } catch (err) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
  }, [currentPage, filters, limit]);

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refresh: loadApplications
  }));

  // Reset to page 1 when limit changes
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await applicationService.updateApplicationStatus(id, newStatus);
      if (onStatusChange) {
        onStatusChange();
      }
      // Refresh the list
      await loadApplications();
    } catch (err) {
      alert(`Failed to update status: ${err.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await applicationService.deleteApplication(id);
        // Refresh the list
        await loadApplications();
        if (onStatusChange) {
          onStatusChange();
        }
      } catch (err) {
        alert(`Failed to delete application: ${err.message}`);
      }
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      accepted: 'success',
      rejected: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '';
    }
  };

  return (
    <div className="email-logs-section">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          {error}
        </Alert>
      )}

      {/* Filters */}
      <div className="mb-3">
        <div className="row g-3">
          <div className="col-md-4">
            <InputGroup>
              <InputGroup.Text>
                <Search size={16} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name, email, position..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-md-3">
            <Form.Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </Form.Select>
          </div>
          <div className="col-md-2">
            <Form.Select
              value={limit}
              onChange={(e) => handleLimitChange(parseInt(e.target.value))}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </Form.Select>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">No applications found.</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Position</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app.id}>
                    <td>{app.fullLegalName}</td>
                    <td>{app.emailAddress}</td>
                    <td>{app.phoneNumber}</td>
                    <td>{app.positionApplyingFor}</td>
                    <td>{getStatusBadge(app.status)}</td>
                    <td>{formatDate(app.createdAt)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => onViewApplication && onViewApplication(app.id)}
                          title="View Application"
                        >
                          <Eye size={16} />
                        </Button>
                        {app.status === 'pending' && (
                          <>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleStatusUpdate(app.id, 'accepted')}
                              title="Accept Application"
                            >
                              <CheckCircle size={16} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleStatusUpdate(app.id, 'rejected')}
                              title="Reject Application"
                            >
                              <XCircle size={16} />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(app.id)}
                          title="Delete Application"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} applications
              </div>
              <Pagination>
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                />
                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
});

ApplicationsList.displayName = 'ApplicationsList';

export default ApplicationsList;

